'use client'

import { Divider } from '@/components/ui/divider'
import { PRODUCT_SIZE } from '@/lib/constants/size'
import { ImageHelper } from '@/lib/helpers/image.helper'
import { VariantMapped } from '@/types/product'
import { ProductDetailFormatted } from '@/types/product-detail'
import { useTranslations } from 'next-intl'
import Image from 'next/image'
import Link from 'next/link'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { InstructionSize } from '../product-info-sizes/instructions-size'
import { ProductInfo } from '../product-info/module'
import { ProductInfoSuggest } from './module'
import { ImageType } from './product-info-suggest-gallery'
import { BasicProductInfo } from './product-info-suggest-modal'

interface ColorOption {
  title: string
  slug: string
  label: string
  image: Array<{ id: string; src: string }>
}

interface ProductInfoSuggestAllItemProps {
  product: BasicProductInfo
  previewToken?: string
}

export const ProductInfoSuggestAllItem = ({
  product,
  previewToken,
}: ProductInfoSuggestAllItemProps) => {
  // State for selected color and size
  const [selectedColorId, setSelectedColorId] = useState<string>('')
  const [selectedSizeId, setSelectedSizeId] = useState<string>('')
  const t = useTranslations('product_detail.instruction_size')

  // Logic to handle gallery images based on selected color
  const galleryImages: ImageType[] = useMemo(() => {
    if (
      !product?.options_value?.[0] ||
      !selectedColorId ||
      !product.options_value[0].options
    )
      return []
    const colorOption = product.options_value[0]?.options?.find(
      (opt: ColorOption) => opt.title === selectedColorId
    )
    const images = colorOption?.image || product?.images || []
    const galleryImages = images.slice(0, -1)
    return galleryImages.map((img: { src: string; id?: string }) => ({
      url: img.src,
      alt: product.display_name || 'Product image',
    }))
  }, [product, selectedColorId])

  // Logic to get color variants
  // eslint-disable-next-line react-hooks/preserve-manual-memoization -- React Compiler isn't enabled in this project; keeping the existing explicit deps to avoid changing recompute behavior
  const colorVariants = useMemo(() => {
    if (!product?.mapped_variants) return []
    const colors = Object.keys(product.mapped_variants || {})
    return colors.map((color) => {
      const firstVariant = product.mapped_variants?.[color]?.[0]
      return {
        imageUrl: firstVariant?.colorImage?.toString() || '',
        title: color,
        id: color,
      }
    })
  }, [product?.mapped_variants])

  // Logic to get available sizes for the selected color
  // eslint-disable-next-line react-hooks/preserve-manual-memoization -- React Compiler isn't enabled in this project; keeping the existing explicit deps to avoid changing recompute behavior
  const availableSizes = useMemo(() => {
    if (!product?.mapped_variants || !selectedColorId) return []
    const variantsForSelectedColor =
      product.mapped_variants[selectedColorId] || []
    const sizeMap = new Map<
      string,
      { isDisabled: boolean; variant: VariantMapped }
    >()
    variantsForSelectedColor.forEach((variant) => {
      if (!variant.size) return
      const sizeKey = variant.size.toString()
      if (
        !sizeMap.has(sizeKey) ||
        (!variant.isDisable && sizeMap.get(sizeKey)?.isDisabled)
      ) {
        sizeMap.set(sizeKey, {
          isDisabled: variant.isDisable || false,
          variant,
        })
      }
    })
    const sizeOrder = ['XS', 'S', 'M', 'L', 'XL', '2XL', '3XL', '4XL']
    const sizeOrderMap = new Map(sizeOrder.map((size, index) => [size, index]))
    return Array.from(sizeMap.entries())
      .map(([size, data]) => ({
        title: size,
        id: size.toLowerCase(),
        isDisabled: data.isDisabled,
      }))
      .sort(
        (a, b) =>
          (sizeOrderMap.get(a.title) ?? Infinity) -
          (sizeOrderMap.get(b.title) ?? Infinity)
      )
  }, [product?.mapped_variants, selectedColorId])

  // Set initial selected color and size
  useEffect(() => {
    if (colorVariants.length > 0 && !selectedColorId) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- initializing default color selection once variants load
      setSelectedColorId(colorVariants[0].id)
    }
  }, [colorVariants, selectedColorId])

  useEffect(() => {
    const firstAvailableSize = availableSizes.find((size) => !size.isDisabled)
    if (
      availableSizes.length > 0 &&
      (!selectedSizeId ||
        !availableSizes.some((s) => s.id === selectedSizeId && !s.isDisabled))
    ) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- initializing default size selection once sizes load/change
      setSelectedSizeId(
        firstAvailableSize ? firstAvailableSize.id : availableSizes[0].id
      )
    }
  }, [availableSizes, selectedSizeId])

  // Handlers
  const handleColorChange = useCallback((colorId: string) => {
    setSelectedColorId(colorId)
  }, [])

  const handleSizeChange = useCallback((sizeId: string) => {
    setSelectedSizeId(sizeId)
  }, [])

  let sizeType = ''
  if (product?.gender_type === 'FEMALE') {
    sizeType = PRODUCT_SIZE[product?.sizechart || '']
  } else {
    sizeType = product?.sizechart || ''
  }

  return (
    <div className='items-stretch gap-11 rounded-2xl border border-neutral-300 p-4 lg:flex'>
      <ProductInfoSuggest.Gallery
        images={galleryImages}
        className='h-[200px] basis-1/2 max-lg:hidden md:flex md:gap-4 lg:h-full [&_.pswp-gallery]:h-full'
      />
      <div className={'flex basis-1/2 flex-col bg-light lg:p-0'}>
        <div>
          <div className='flex gap-4 rounded-lg'>
            <Image
              src={ImageHelper.getImageUrl(
                product?.default_thumbnail || '',
                '',
                136
              )}
              alt={product?.display_name || 'Product'}
              width={68}
              height={68}
              className='h-auto rounded object-contain lg:hidden'
            />
            <div>
              {typeof product.tag_text === 'string' && product.tag_text ? (
                <div className='mb-1 w-fit rounded-lg bg-primary-500 px-2 py-1 text-xs font-bold uppercase text-white'>
                  {product.tag_text}
                </div>
              ) : null}
              <Link
                href={`/product/${product.seo_alias}`}
                className='line-clamp-2 font-sans text-sm text-neutral-900'
              >
                {product?.display_name}
              </Link>
              <div className='md:hidden'>
                <ProductInfo.Price
                  product={product as ProductDetailFormatted}
                  comparePrice={product?.compare_price || 0}
                  regularPrice={product?.regular_price || 0}
                  salePercent={product?.percent || 0}
                />
              </div>
            </div>
          </div>
          <Divider className='my-4 bg-neutral-200' />
          <div className='max-md:hidden'>
            <ProductInfo.Price
              product={product as ProductDetailFormatted}
              comparePrice={product?.compare_price || 0}
              regularPrice={product?.regular_price || 0}
              salePercent={product?.percent || 0}
            />
          </div>
        </div>
        <div className='space-y-4 lg:mt-10'>
          <ProductInfoSuggest.Variants
            variants={colorVariants}
            selectedVariantId={selectedColorId}
            onVariantChange={handleColorChange}
          />
          <ProductInfoSuggest.Sizes
            sizes={availableSizes}
            selectedSizeId={selectedSizeId}
            onSizeChange={handleSizeChange}
          />
          {/* <div className='text-sm text-neutral-500 underline'>
            {t('choose_size')}
          </div> */}
          <InstructionSize
            sizeChart={sizeType}
            gender_type={product.gender_type || ''}
            size_guide={
              product.size_guide || {
                rows: [],
                columns: [],
                headerTitle: '',
                leftTitle: '',
                matrix: [],
              }
            }
            productId={product.id}
            product={product as ProductDetailFormatted}
          />
          {!previewToken ? (
            <ProductInfoSuggest.AddCart
              size={selectedSizeId}
              color={selectedColorId}
              product={product}
            />
          ) : null}
        </div>
      </div>
    </div>
  )
}
